import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiActivity, FiCpu, FiWifi, FiCheckCircle, FiAlertCircle, FiRefreshCw } = FiIcons;

export default function StreamHealthMonitor({ settings }) {
  const [metrics, setMetrics] = useState({
    status: 'OPTIMAL',
    bitrate: '3,850 kbps',
    fps: '60 fps',
    resolution: '1080p (Full HD)',
    bufferHealth: '3.8s',
    droppedFrames: '0.02%',
    latency: '1.4s',
    audioQuality: '192 kbps AAC'
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!settings?.is_active) {
        setMetrics((prev) => ({ ...prev, status: 'OFFLINE', bitrate: '0 kbps', fps: '0 fps' }));
        return;
      }
      const randomBitrate = Math.floor(3700 + Math.random() * 350);
      const randomBuffer = (3.2 + Math.random() * 1.2).toFixed(1);
      const randomLatency = (1.2 + Math.random() * 0.5).toFixed(1);
      setMetrics({
        status: 'OPTIMAL',
        bitrate: `${randomBitrate.toLocaleString()} kbps`,
        fps: '60 fps',
        resolution: '1920x1080 (60fps)',
        bufferHealth: `${randomBuffer}s`,
        droppedFrames: '0.01%',
        latency: `${randomLatency}s`,
        audioQuality: '192 kbps AAC'
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [settings?.is_active]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 600);
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#111] p-5 space-y-5">
      <div className="flex items-center justify-between border-b border-white/5 pb-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-500/10 text-green-400">
            <SafeIcon icon={FiActivity} size={18} />
          </span>
          <div>
            <h3 className="text-sm font-bold text-white sm:text-base flex items-center gap-2">
              Monitor de Salud de Señal
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${settings?.is_active ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400'}`}>
                {settings?.is_active ? 'SEÑAL SALUDABLE' : 'TRANSMISIÓN INACTIVA'}
              </span>
            </h3>
            <p className="text-xs text-gray-500">Telemetría de la señal HLS transmitida a los espectadores</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          title="Recalcular métricas"
        >
          <SafeIcon icon={FiRefreshCw} size={14} className={isRefreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-black/40 border border-white/5 p-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase">Tasa de Bits</p>
          <p className="text-sm sm:text-base font-bold text-white mt-1">{metrics.bitrate}</p>
          <span className="text-[9px] text-green-400 flex items-center gap-1 mt-0.5">
            <SafeIcon icon={FiCheckCircle} size={9} /> Estable
          </span>
        </div>

        <div className="rounded-xl bg-black/40 border border-white/5 p-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase">Fotogramas</p>
          <p className="text-sm sm:text-base font-bold text-white mt-1">{metrics.fps}</p>
          <span className="text-[9px] text-gray-400 mt-0.5">Pérdida: {metrics.droppedFrames}</span>
        </div>

        <div className="rounded-xl bg-black/40 border border-white/5 p-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase">Buffer & Latencia</p>
          <p className="text-sm sm:text-base font-bold text-white mt-1">{metrics.latency}</p>
          <span className="text-[9px] text-green-400 mt-0.5">Buffer: {metrics.bufferHealth}</span>
        </div>

        <div className="rounded-xl bg-black/40 border border-white/5 p-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase">Calidad Audio</p>
          <p className="text-sm sm:text-base font-bold text-white mt-1">{metrics.audioQuality}</p>
          <span className="text-[9px] text-blue-400 mt-0.5">Stereo 48kHz</span>
        </div>
      </div>
    </section>
  );
}